import { Button, Checkbox, Container, Label } from "@medusajs/ui"
import { useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { EmptyState, ErrorState, PageHeader, SearchInput } from "@/components/ui"
import { useActivity, type ActivityParams } from "@/lib/api"
import { describeAction, initials } from "@/lib/activity-text"
import { AREA_LABELS, AREAS, type ActivityEntry } from "@/lib/types"
import { Badge, DateRange, FilterBar, FilterSelect, LoadingCard } from "@/terabizz"

const PAGE = 50

const iso = (d: Date) => d.toISOString().slice(0, 10)
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86_400_000))
const PRESETS: [string, string][] = [
  ["today", "Today"],
  ["7d", "Last 7 days"],
  ["30d", "Last 30 days"],
  ["90d", "Last 90 days"],
  ["custom", "Custom"],
]
function presetRange(p: string | null): { from?: string; to?: string } {
  if (p === "today") return { from: daysAgo(0) }
  if (p === "7d") return { from: daysAgo(6) }
  if (p === "30d") return { from: daysAgo(29) }
  if (p === "90d") return { from: daysAgo(89) }
  return {}
}

const dayKey = (isoStr: string) => new Date(isoStr).toLocaleDateString("en-CA")
function dayLabel(key: string) {
  const today = new Date().toLocaleDateString("en-CA")
  const yesterday = new Date(Date.now() - 86_400_000).toLocaleDateString("en-CA")
  if (key === today) return "Today"
  if (key === yesterday) return "Yesterday"
  return new Date(`${key}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })
}
const time = (isoStr: string) => new Date(isoStr).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })

/**
 * Settings → Store activity log, Shopify-style: one readable line per change
 * ("Jatin Maurya updated product Bacteriostatic Water"), grouped by day, with search,
 * staff / area / result / date filters and a details row (request, status, IP).
 * Filters live in the URL so a filtered view can be shared or reloaded.
 */
export function ActivityPage() {
  const [params, setParams] = useSearchParams()
  const get = (k: string) => params.get(k) ?? ""
  const page = Math.max(0, Number(params.get("page") ?? 1) - 1)
  const preset = params.get("range") ?? "30d"
  const custom = preset === "custom"
  const range = custom ? { from: get("from") || undefined, to: get("to") || undefined } : presetRange(preset)
  const query: ActivityParams = {
    limit: PAGE,
    offset: page * PAGE,
    q: get("q") || undefined,
    actor_id: get("staff") || undefined,
    area: get("area") || undefined,
    result: (get("result") || undefined) as ActivityParams["result"],
    hide_sign_ins: params.get("signins") === "1" ? undefined : "1",
    ...range,
  }
  const { data, isLoading, isFetching, error } = useActivity(query)
  // The staff list arrives with the first page; keep it while paging.
  const [actors, setActors] = useState<{ id: string; email: string | null; name: string | null }[]>([])
  if (data?.actors && data.actors !== actors && data.actors.length !== actors.length) setActors(data.actors)

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k)
    if (!("page" in patch)) next.delete("page")
    setParams(next, { replace: true })
  }
  const filtered = !!(get("q") || get("staff") || get("area") || get("result"))

  const groups = useMemo(() => {
    const out: { key: string; rows: ActivityEntry[] }[] = []
    for (const a of data?.activity ?? []) {
      const k = dayKey(a.created_at)
      const g = out[out.length - 1]
      if (g && g.key === k) g.rows.push(a)
      else out.push({ key: k, rows: [a] })
    }
    return out
  }, [data])

  const count = data?.count ?? 0
  const pages = Math.max(1, Math.ceil(count / PAGE))

  return (
    <>
      <PageHeader title="Activity log" />
      <Container className="tf-card overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 [&>div]:max-w-none">
          <SearchInput value={get("q")} onChange={(v) => set({ q: v || null })} placeholder="Search by record, staff email or action" />
        </div>
        <FilterBar>
          <FilterSelect label="Date" value={preset} onChange={(v) => set({ range: v === "30d" ? null : v, from: null, to: null })} options={PRESETS.concat([["all", "All time"]])} allowAll={false} />
          {custom && <DateRange from={get("from")} to={get("to")} onChange={(p) => set({ ...(p.from !== undefined ? { from: p.from } : {}), ...(p.to !== undefined ? { to: p.to } : {}) })} />}
          <FilterSelect label="Staff" value={get("staff")} onChange={(v) => set({ staff: v || null })} options={actors.map((a) => [a.id, a.name || a.email || a.id])} />
          <FilterSelect label="Area" value={get("area")} onChange={(v) => set({ area: v || null })} options={AREAS.map((a) => [a, AREA_LABELS[a]])} />
          <FilterSelect
            label="Result"
            value={get("result")}
            onChange={(v) => set({ result: v || null })}
            options={[
              ["ok", "Done"],
              ["failed", "Failed"],
              ["blocked", "Blocked"],
            ]}
          />
          <div className="flex items-center gap-x-2 pl-1">
            <Checkbox id="signins" checked={params.get("signins") === "1"} onCheckedChange={(c) => set({ signins: c === true ? "1" : null })} />
            <Label htmlFor="signins" className="txt-compact-small cursor-pointer text-[var(--tb-ink-3)]">
              Show sign-ins
            </Label>
          </div>
          {filtered && (
            <button type="button" className="txt-compact-small ml-auto text-ui-fg-interactive hover:underline" onClick={() => set({ q: null, staff: null, area: null, result: null })}>
              Clear filters
            </button>
          )}
        </FilterBar>

        {error ? (
          <ErrorState error={error} />
        ) : isLoading ? (
          <div className="p-4">
            <LoadingCard lines={6} />
          </div>
        ) : !groups.length ? (
          <EmptyState title="No activity found" body={filtered || preset !== "all" ? "Try a longer date range or clear the filters." : "Changes staff make will show up here."} />
        ) : (
          <div className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"} aria-busy={isFetching}>
            {groups.map((g) => (
              <section key={g.key} aria-label={dayLabel(g.key)}>
                <h2 className="sticky top-0 z-[1] border-t border-[var(--tb-line)] bg-[var(--tb-canvas)] px-4 py-1.5 text-[12px] font-medium text-[var(--tb-ink-3)]">{dayLabel(g.key)}</h2>
                <ul>
                  {g.rows.map((a) => (
                    <ActivityRow key={a.id} a={a} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        {count > 0 && (
          <div className="flex items-center justify-between border-t border-[var(--tb-line)] px-4 py-2.5">
            <span className="txt-compact-small text-[var(--tb-ink-3)] tabular-nums">
              {page * PAGE + 1}–{Math.min(count, (page + 1) * PAGE)} of {count.toLocaleString()}
            </span>
            <div className="flex gap-x-2">
              <Button size="small" variant="secondary" disabled={page === 0} onClick={() => set({ page: page > 1 ? String(page) : null })}>
                Previous
              </Button>
              <Button size="small" variant="secondary" disabled={page + 1 >= pages} onClick={() => set({ page: String(page + 2) })}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Container>
    </>
  )
}

function Result({ status }: { status: number | null }) {
  if (status == null || status < 400) return null
  if (status === 403) return <Badge tone="attention">Blocked</Badge>
  return <Badge tone="critical">Failed · {status}</Badge>
}

function ActivityRow({ a }: { a: ActivityEntry }) {
  const [open, setOpen] = useState(false)
  const who = a.actor_name || a.actor_email || (a.actor_type === "api-key" ? "API key" : "System")
  const { verb, noun } = describeAction(a.action, a.method)
  const rec = a.record
  const kind = (rec?.kind ?? "").toLowerCase()
  // "updated product Bacteriostatic Water"; for nested actions "created fulfilment on order #1042"
  const onParent = rec?.kind && noun && noun !== kind && !kind.includes(noun)
  const recordText = rec ? rec.label ?? rec.id : null
  const recordNode = recordText ? (
    rec?.url ? (
      <Link to={rec.url} className="font-medium text-[var(--tb-ink)] hover:underline" onClick={(e) => e.stopPropagation()}>
        {recordText}
      </Link>
    ) : (
      <span className="font-medium text-[var(--tb-ink)]">
        {recordText}
        {rec?.deleted ? <span className="font-normal text-[var(--tb-ink-3)]"> (deleted)</span> : null}
      </span>
    )
  ) : null
  const signIn = a.action === "staff.signed_in"

  return (
    <li className="border-t border-[var(--tb-line)] first:border-t-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-start gap-x-3 px-4 py-2.5 text-left hover:bg-[var(--tb-surface-hover,#f7f7f7)]">
        <span aria-hidden className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--tb-canvas)] text-[11px] font-semibold text-[var(--tb-ink-2)]">
          {a.actor_type === "api-key" ? "API" : initials(a.actor_name, a.actor_email)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] leading-5 text-[var(--tb-ink-2)]">
            <span className="font-medium text-[var(--tb-ink)]">{who}</span>{" "}
            {signIn ? (
              "signed in"
            ) : (
              <>
                {onParent && recordNode ? (
                  // nested resource: "added product to sales channel X", "created fulfilment on order #1042"
                  <>
                    {verb === "created" ? `added ${noun} to` : verb === "deleted" ? `removed ${noun} from` : `${verb} ${noun} on`} {kind} {recordNode}
                  </>
                ) : (
                  <>
                    {verb} {kind || noun}
                    {recordNode ? <> {recordNode}</> : null}
                  </>
                )}
              </>
            )}
          </span>
          <span className="mt-0.5 block text-[12px] text-[var(--tb-ink-3)]">
            {time(a.created_at)}
            {a.area ? ` · ${AREA_LABELS[a.area] ?? a.area}` : ""}
          </span>
        </span>
        <span className="shrink-0 pt-0.5">
          <Result status={a.status_code} />
        </span>
      </button>
      {open && (
        <dl className="mx-4 mb-3 ml-14 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-[8px] bg-[var(--tb-canvas)] px-3 py-2.5 text-[12px]">
          <dt className="text-[var(--tb-ink-3)]">When</dt>
          <dd className="tabular-nums">{new Date(a.created_at).toLocaleString()}</dd>
          <dt className="text-[var(--tb-ink-3)]">Staff</dt>
          <dd>{a.actor_email ?? a.actor_id ?? "—"}</dd>
          <dt className="text-[var(--tb-ink-3)]">Action</dt>
          <dd className="font-mono">{a.action}</dd>
          {a.method && a.path ? (
            <>
              <dt className="text-[var(--tb-ink-3)]">Request</dt>
              <dd className="break-all font-mono">
                {a.method} {a.path}
              </dd>
            </>
          ) : null}
          {rec ? (
            <>
              <dt className="text-[var(--tb-ink-3)]">Record</dt>
              <dd className="break-all font-mono">{rec.id}</dd>
            </>
          ) : null}
          <dt className="text-[var(--tb-ink-3)]">Result</dt>
          <dd>{a.status_code == null ? "Recorded" : a.status_code < 400 ? `Done (${a.status_code})` : a.status_code === 403 ? "Blocked: missing permission (403)" : `Failed (${a.status_code})`}</dd>
          {a.ip ? (
            <>
              <dt className="text-[var(--tb-ink-3)]">IP address</dt>
              <dd className="font-mono">{a.ip}</dd>
            </>
          ) : null}
        </dl>
      )}
    </li>
  )
}
